import { useCallback, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SettingsRow } from '@reflex/ui/components/shared/SettingsRow';
import { Switch } from '@reflex/ui/components/ui/switch';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import {
  useNotificationPrefs,
  getNotificationPermission,
  type NotificationPermissionState,
} from '@/hooks/browser/useNotificationPrefs';

export function NotificationsSettingsCard() {
  const { prefs, update } = useNotificationPrefs();
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    getNotificationPermission(),
  );

  const unsupported = permission === 'unsupported';
  const blocked = permission === 'denied';
  const masterDisabled = unsupported || blocked;

  const handleEnableToggle = useCallback(
    async (next: boolean) => {
      if (!next) {
        update('enabled', false);
        return;
      }
      if (typeof Notification === 'undefined') {
        toast.error('Notifications are not supported in this browser');
        return;
      }
      if (Notification.permission === 'granted') {
        setPermission('granted');
        update('enabled', true);
        return;
      }
      if (Notification.permission === 'denied') {
        setPermission('denied');
        toast.error('Notifications are blocked', {
          description: 'Allow notifications for this site in your browser settings to enable.',
        });
        return;
      }
      try {
        const result = await Notification.requestPermission();
        setPermission(result as NotificationPermissionState);
        if (result === 'granted') {
          update('enabled', true);
        } else if (result === 'denied') {
          toast.error('Notifications were blocked');
        }
      } catch {
        toast.error('Could not request notification permission');
      }
    },
    [update],
  );

  const enabled = prefs.enabled && permission === 'granted';
  const Icon = enabled ? Bell : BellOff;

  return (
    <SectionCard
      title="Desktop Notifications"
      description="Get a system notification when your agents change state. Only fires while this tab is open."
      icon={<Icon />}
      action={
        <Switch
          checked={enabled}
          disabled={masterDisabled}
          onCheckedChange={(v) => void handleEnableToggle(v)}
          aria-label="Enable desktop notifications"
        />
      }
      contentClassName="space-y-4"
    >
      {unsupported && (
        <PageDescription className="mt-0 text-xs">
          Your browser does not support desktop notifications.
        </PageDescription>
      )}
      {blocked && (
        <PageDescription className="mt-0 text-xs">
          Notifications are blocked for this site. Update your browser site settings to enable them.
        </PageDescription>
      )}
      <div className="space-y-3">
        <NotificationToggleRow
          label="Agent needs input"
          description="When an agent pauses to wait on your reply."
          checked={prefs.onNeedsInput}
          disabled={!enabled}
          onChange={(v) => update('onNeedsInput', v)}
        />
        <NotificationToggleRow
          label="Errors"
          description="When an agent fails to start or errors during a run."
          checked={prefs.onError}
          disabled={!enabled}
          onChange={(v) => update('onError', v)}
        />
        <NotificationToggleRow
          label="Completed"
          description="When an agent finishes its task."
          checked={prefs.onCompleted}
          disabled={!enabled}
          onChange={(v) => update('onCompleted', v)}
        />
      </div>
    </SectionCard>
  );
}

interface NotificationToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function NotificationToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: NotificationToggleRowProps) {
  return (
    <SettingsRow
      variant="plain"
      as="h3"
      title={label}
      description={description}
      control={
        <Switch
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
          aria-label={label}
        />
      }
    />
  );
}
